/**
 * Entrada de anexos por colagem e por arrasto. Tudo aqui é função pura: o
 * estado e os ouvintes ficam em [useAnexos].
 */

/**
 * O navegador entrega toda imagem colada com o mesmo nome — "image.png" —, e no
 * banco só sobra a denominação: dois prints no mesmo chamado ficariam
 * indistinguíveis. Arquivo vindo do Explorer ou do Finder chega com nome
 * próprio, e esse é preservado.
 */
const NOME_GENERICO = /^(image|imagem)(\s*\(\d+\))?\.[a-z0-9]+$/i;

const extensao = (arquivo: File): string => {
  const doNome = arquivo.name.match(/\.([a-z0-9]+)$/i)?.[1];
  if (doNome) return doNome.toLowerCase();

  // "image/jpeg" → jpg, que é a extensão que o Windows entende.
  const subtipo = arquivo.type.split("/")[1]?.split("+")[0]?.toLowerCase() ?? "";
  if (!subtipo) return "bin";
  return subtipo === "jpeg" ? "jpg" : subtipo;
};

const carimbo = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  );
};

const renomear = (arquivo: File, nome: string): File =>
  nome === arquivo.name
    ? arquivo
    : new File([arquivo], nome, {
        type: arquivo.type,
        lastModified: arquivo.lastModified,
      });

export const nomeDeColagem = (arquivo: File, agora: Date): string =>
  arquivo.name && !NOME_GENERICO.test(arquivo.name)
    ? arquivo.name
    : `imagem-${carimbo(agora)}.${extensao(arquivo)}`;

/** Só o que interessa de um ClipboardEvent ou DragEvent. */
export interface Transferencia {
  files: ArrayLike<File>;
  getData(tipo: string): string;
}

/** Arquivos de uma colagem ou de um arrasto, já com nome utilizável. */
export const arquivosDeTransferencia = (
  dt: Transferencia | null | undefined,
  agora = new Date()
): File[] =>
  Array.from(dt?.files ?? []).map((f) => renomear(f, nomeDeColagem(f, agora)));

/**
 * Colar do Word, do Excel ou do Outlook às vezes traz só o HTML, sem o binário
 * da imagem: não há o que anexar, e ficar em silêncio faz a pessoa acreditar
 * que colou. Se veio texto junto, a colagem era de texto mesmo.
 */
export const colagemSemBinario = (dt: Transferencia | null | undefined): boolean => {
  if (!dt || dt.files.length > 0) return false;
  if (dt.getData("text/plain").trim()) return false;
  return /<img\b/i.test(dt.getData("text/html"));
};

/**
 * Junta o que chegou ao que já está escolhido. Arquivo idêntico — mesmo nome,
 * tamanho e data — é o mesmo arquivo escolhido duas vezes e é descartado; nome
 * repetido entre arquivos diferentes ganha sufixo " (2)", porque a denominação
 * é tudo o que distingue um anexo do outro depois de gravado.
 */
export const juntarAnexos = (atuais: File[], novos: File[]): File[] => {
  const identidade = (a: File) => `${a.name}|${a.size}|${a.lastModified}`;
  const jaEscolhidos = new Set(atuais.map(identidade));
  const nomesUsados = new Set(atuais.map((a) => a.name));

  const aceitos: File[] = [];
  for (const arquivo of novos) {
    if (jaEscolhidos.has(identidade(arquivo))) continue;
    jaEscolhidos.add(identidade(arquivo));

    if (!nomesUsados.has(arquivo.name)) {
      nomesUsados.add(arquivo.name);
      aceitos.push(arquivo);
      continue;
    }

    const ponto = arquivo.name.lastIndexOf(".");
    const base = ponto > 0 ? arquivo.name.slice(0, ponto) : arquivo.name;
    const ext = ponto > 0 ? arquivo.name.slice(ponto) : "";
    let n = 2;
    while (nomesUsados.has(`${base} (${n})${ext}`)) n++;

    const nome = `${base} (${n})${ext}`;
    nomesUsados.add(nome);
    aceitos.push(renomear(arquivo, nome));
  }

  return [...atuais, ...aceitos];
};
